import { useState, useEffect } from 'react'
import type { ReactNode } from 'react'
import { assessCredit, treatmentOf } from '../../lib/gstTreatment'
import { supabase } from '../../lib/supabase'
import { STATUS_KEYS, statusColor, statusLabel } from '../../lib/dealStatusService'
import { ON_HAND_STATUSES, SOLD_STATUSES, isSoldStatus } from '../../lib/dealLifecycle'
import { Deal, User } from '../../types'
import DealWorkspace from './DealWorkspace'
import './StockManagementView.css'

/**
 * Stock Management — the vehicle register.
 *
 * Layout follows the Easy Cars "Stock Management > List" screen so operators
 * moving across from Easy Cars recognise it: tab strip with create buttons,
 * a wide search bar with Search / Advanced Search, a stage dropdown, a
 * Quick Search chip row, a zebra-striped table and a pagination footer.
 *
 * Differences from Easy Cars, all deliberate:
 *   - Yard Code column removed (single-yard operation).
 *   - VIN# column added in its place.
 *   - Rego No, New/Used and Advertised Price omitted — Direct Auto does not
 *     hold that data yet, and valuation_amount is an Autograb valuation, not
 *     an advertised price, so showing it in that column would mislead.
 *
 * Every filter and chip here runs against real deal data. The two create
 * buttons and Advanced Search are visual placeholders and say so when clicked.
 */

/*
 * The stage list is the canonical registry, filtered to the statuses this screen
 * is entitled to show.
 *
 * It used to be fourteen hand-written rows with their own labels and their own
 * hex colours, and three of those labels disagreed with the rest of the platform:
 * Under Offer against Offer Accepted, Paid against Paid (Ready for Sale), and In
 * Stock against Listed. Winnie renamed Under Offer on the Deal Pipeline and the
 * rename never reached this screen, so the same car read one thing on the board
 * and another on the stock register. That is the whole reason the Vehicle Status
 * Registry spec exists.
 */
const STOCK_SURFACE_STATUSES: readonly string[] = [...ON_HAND_STATUSES, ...SOLD_STATUSES]
const STAGES: { id: string; label: string; color: string }[] = STATUS_KEYS.filter((k) =>
  STOCK_SURFACE_STATUSES.includes(k),
).map((k) => ({ id: k, label: statusLabel(k), color: statusColor(k) }))

/* From 10 Sep a sale lands on sold_in_transit or sold_delivered rather than plain
   'sold'. All of them are gone from the yard, so all of them read as Sold here. */
const SOLD_STAGES: readonly string[] = SOLD_STATUSES

/**
 * What counts as stock.
 *
 * Winnie's review: only SOLD and IN STOCK vehicles belong in this list — a deal
 * still being negotiated is not stock. "In stock" is paid-or-listed, matching
 * the dashboard's on-hand definition: once the car is paid for it is yours,
 * whether or not it has been listed yet. Using the stricter listed-only reading
 * would have hidden every paid-but-unlisted vehicle from the only stock register
 * in the platform.
 */
/* Deal Pending is a buyer's promise, not a delivery - the car is still ours and still
   in the yard, so it is stock until it actually leaves. */
const IN_STOCK_STAGES: readonly string[] = ON_HAND_STATUSES
const STOCK_REGISTER_STAGES = [...IN_STOCK_STAGES, ...SOLD_STAGES]

// Quick Search is Active / Sold / All only, per Winnie's review. The narrower
// exception chips (Deposit Taken, No Images, No PPSR Report) were removed; the
// row flags for those conditions stay, so nothing becomes invisible.
type Chip = 'active' | 'sold' | 'all'

const CHIPS: { id: Chip; label: string }[] = [
  { id: 'active', label: 'Active' },
  { id: 'sold', label: 'Sold' },
  { id: 'all', label: 'All' },
]

type SortKey = string

export default function StockManagementView({ user }: { user: User }) {
  const [stock, setStock] = useState<Deal[]>([])
  const [loading, setLoading] = useState(true)
  const [typed, setTyped] = useState('')
  const [applied, setApplied] = useState('')
  const [stage, setStage] = useState('all')
  /*
   * Opens on All. The register is now the full stock book loaded from the
   * EasyCars/Xero reconciliation, which is almost entirely sold vehicles, so
   * an Active-first default showed one car and looked broken. Active and Sold
   * are still one click away.
   */
  const [chip, setChip] = useState<Chip>('all')
  // Nick, 1 Sept: the register reads oldest to current. A stock book is walked
  // forwards — the car bought first is the first line — so the default sort is
  // purchase date ascending, not days-in-stock.
  const [sortKey, setSortKey] = useState<SortKey>('purchased')
  const [sortAsc, setSortAsc] = useState(true)
  const [page, setPage] = useState(1)
  // Winnie's review: show 100 by default rather than 10.
  const [pageSize, setPageSize] = useState(100)
  const [compact, setCompact] = useState(false)
  const [selectedDeal, setSelectedDeal] = useState<Deal | null>(null)
  const [banner, setBanner] = useState<string | null>(null)
  const [colFilters, setColFilters] = useState<Record<string, string>>({})

  useEffect(() => {
    fetchStock()
  }, [])

  const fetchStock = async () => {
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('deals')
        .select('*')
        .is('deleted_at', null)
        .in('deal_status', STOCK_REGISTER_STAGES)
        .order('created_at', { ascending: true })
      if (error) throw error
      setStock(data || [])
    } catch (err) {
      console.error('Error fetching stock:', err)
      setBanner('Could not load stock. Refresh to try again.')
    } finally {
      setLoading(false)
    }
  }

  const dVin = (d: Deal) => d.vehicle_vin || d.autograb_vin || ''
  const dRego = (d: Deal) => d.registration_number || ''
  const dCondition = (d: Deal) => d.vehicle_condition || 'Used'
  const dYear = (d: Deal) => d.autograb_year || d.vehicle_year || null
  const dMake = (d: Deal) => d.autograb_make || d.vehicle_make || ''
  const dModel = (d: Deal) => d.autograb_model || d.vehicle_model || ''
  const dColour = (d: Deal) => d.vehicle_color || d.autograb_color || ''
  const dOdo = (d: Deal) => {
    const v = d.vehicle_odometer != null ? d.vehicle_odometer : d.autograb_odometer
    return v != null ? v : null
  }

  /*
   * A real stock number, falling back to the first six characters of the id for
   * rows that predate the sequence, so nothing goes blank.
   */
  const stockNo = (d: Deal) =>
    d.stock_number != null ? String(d.stock_number) : d.id.slice(0, 6).toUpperCase()

  const spec = (d: Deal) =>
    [d.autograb_body_type, d.autograb_transmission, d.autograb_engine, d.autograb_fuel_type]
      .filter(Boolean)
      .join(' \u00b7 ')

  /*
   * Purchase date, purchase price, sold date and sold price. The purchase date
   * follows the same order of preference the GST register uses, so the two
   * screens name the same day for the same car.
   */
  const purchasedOn = (d: Deal) => d.acquired_date || d.paid_at || d.created_at || null
  const purchasePriceOf = (d: Deal) =>
    d.valuation_amount != null ? Number(d.valuation_amount) : null
  const soldOn = (d: Deal) => d.sale_date || d.sold_at || null
  /*
   * The sale price ONLY once the vehicle has actually sold.
   *
   * This used to hand `sale_price` to assessCredit raw. saveProposal writes a sale price
   * without moving the status ("Does not move the status", saleService), so a car still on
   * the lot with a proposal on file showed a realised, capped Margin Scheme Credit on this
   * screen while the GST register correctly showed zero - two screens disagreeing about a BAS
   * figure. Spec section 3: a NITC vehicle at paid_ready_for_sale or deal_pending carries a
   * credit of zero until it sells. Same defect the register was fixed for on 13 September,
   * found here by the 14 September spec audit.
   */
  const salePriceOf = (d: Deal) =>
    isSoldStatus(d.deal_status) && d.sale_price != null ? Number(d.sale_price) : null
  const asDate = (v?: string | null) => (v ? String(v).slice(0, 10) : '')

  /*
   * Days in stock, on the reconciliation's definition: purchase to sale for a car
   * that has sold, purchase to today for one still on the floor. The old figure
   * counted created_at to today, so a car bought and sold in March read as the
   * months since we bought it rather than the days we actually held it.
   */
  const days = (d: Deal) => {
    const from = purchasedOn(d)
    if (!from) return 0
    const to = soldOn(d)
    const ms = (to ? new Date(to).getTime() : Date.now()) - new Date(from).getTime()
    return Math.max(0, Math.floor(ms / 86400000))
  }

  /* A status outside this screen's list still gets the registry's label and colour,
     never the raw database key printed at a person. */
  const stageOf = (d: Deal) =>
    STAGES.find((s) => s.id === d.deal_status) || {
      id: d.deal_status,
      label: statusLabel(d.deal_status),
      color: statusColor(d.deal_status),
    }

  /*
   * The tax type in the reconciliation's own wording, so this column reads the
   * same here as in the spreadsheet it is checked against.
   */
  const taxTypeLabel = (d: Deal) => {
    const t = treatmentOf(d)
    if (t === 'no_gst') return 'N/A - purchase cancelled/vehicle returned'
    if (t === 'notional_itc') return 'Private individual -Non-registered (Notional ITC)'
    return d.seller_type === 'company'
      ? 'Private Company (ABN) - Tax Inv Provided'
      : 'LMCT/Auction/Tender/Fleet (ABN) - Tax Inv Provided'
  }

  /*
   * Gross, GST, G1 and Notional come from assessCredit, the same function the GST
   * register uses, rather than a second copy of the arithmetic living here. That
   * is what makes the Division 66 cap show up on this screen too: where a car
   * sold for less than it cost, the notional credit is limited to the GST on the
   * sale, and the register and the stock list agree about it.
   */
  const gstFacts = (d: Deal) => {
    const gross = purchasePriceOf(d) || 0
    const a = assessCredit(d, gross, { salePrice: salePriceOf(d), saleDate: soldOn(d) })
    const known = gross > 0
    return {
      gross: known ? gross : null,
      gst: known ? (a.treatment === 'tax_invoice' ? a.notional : 0) : null,
      marginSchemeCredit: known ? a.marginSchemeCredit : null,
      notional: known ? a.claimable : null,
    }
  }

  const money0 = (v: number | null) => (v == null ? '' : '$' + Math.round(v).toLocaleString('en-AU'))
  const money2 = (v: number | null) =>
    v == null
      ? ''
      : '$' + v.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

  /*
   * One definition per column, driving the header, the per-column filter row, the
   * cells, the sort and the CSV export from a single place. These used to be five
   * hand-written lists that had to be kept in step by hand.
   *
   * The columns and their order follow the EasyCars/Xero stock reconciliation, so
   * this screen and that spreadsheet can be read side by side.
   */
  type Col = {
    key: string
    label: string
    num?: boolean
    text: (d: Deal) => string
    sort: (d: Deal) => string | number
    render?: (d: Deal) => ReactNode
  }

  const COLUMNS: Col[] = [
    { key: 'stock', label: 'Stock No', text: (d) => stockNo(d), sort: (d) => Number(d.stock_number ?? 0) },
    { key: 'rego', label: 'Rego', text: (d) => dRego(d), sort: (d) => dRego(d) },
    { key: 'vin', label: 'VIN', text: (d) => dVin(d), sort: (d) => dVin(d) },
    { key: 'year', label: 'Year', num: true, text: (d) => String(dYear(d) ?? ''), sort: (d) => Number(dYear(d) ?? 0) },
    { key: 'make', label: 'Make', text: (d) => dMake(d), sort: (d) => dMake(d) },
    { key: 'model', label: 'Model', text: (d) => dModel(d), sort: (d) => dModel(d) },
    { key: 'colour', label: 'Colour', text: (d) => dColour(d), sort: (d) => dColour(d) },
    {
      key: 'odometer',
      label: 'Odometer',
      num: true,
      text: (d) => (dOdo(d) != null ? (dOdo(d) as number).toLocaleString('en-AU') : ''),
      sort: (d) => dOdo(d) ?? 0,
    },
    { key: 'condition', label: 'New/Used', text: (d) => dCondition(d), sort: (d) => dCondition(d) },
    {
      key: 'status',
      label: 'Status',
      text: (d) => stageOf(d).label,
      sort: (d) => stageOf(d).label,
      render: (d) => (
        <span className="stock-status" style={{ background: stageOf(d).color }}>
          {stageOf(d).label}
        </span>
      ),
    },
    { key: 'purchased', label: 'Purchase Date', text: (d) => asDate(purchasedOn(d)), sort: (d) => purchasedOn(d) || '' },
    { key: 'purchasePrice', label: 'Purchase Price', num: true, text: (d) => money0(purchasePriceOf(d)), sort: (d) => purchasePriceOf(d) ?? 0 },
    { key: 'sold', label: 'Sold Date', text: (d) => asDate(soldOn(d)), sort: (d) => soldOn(d) || '' },
    { key: 'soldPrice', label: 'Sold Price', num: true, text: (d) => money0(salePriceOf(d)), sort: (d) => salePriceOf(d) ?? 0 },
    { key: 'days', label: 'Days in Stock', num: true, text: (d) => String(days(d)), sort: (d) => days(d) },
    { key: 'reference', label: 'Reference', text: (d) => d.purchase_reference || '', sort: (d) => d.purchase_reference || '' },
    { key: 'gross', label: 'Gross', num: true, text: (d) => money0(gstFacts(d).gross), sort: (d) => gstFacts(d).gross ?? 0 },
    { key: 'gst', label: 'GST', num: true, text: (d) => money2(gstFacts(d).gst), sort: (d) => gstFacts(d).gst ?? 0 },
    { key: 'taxType', label: 'GST Treatment (Tax Type)', text: (d) => taxTypeLabel(d), sort: (d) => taxTypeLabel(d) },
    { key: 'marginSchemeCredit', label: 'Margin Scheme Credit', num: true, text: (d) => money0(gstFacts(d).marginSchemeCredit), sort: (d) => gstFacts(d).marginSchemeCredit ?? 0 },
    { key: 'notional', label: 'Notional', num: true, text: (d) => money2(gstFacts(d).notional), sort: (d) => gstFacts(d).notional ?? 0 },
  ]

  const matchesChip = (d: Deal) => {
    switch (chip) {
      case 'active':
        return IN_STOCK_STAGES.includes(d.deal_status)
      case 'sold':
        return SOLD_STAGES.includes(d.deal_status)
      default:
        return true
    }
  }

  const matchesSearch = (d: Deal) => {
    if (!applied) return true
    const t = applied.toLowerCase()
    return [
      stockNo(d),
      dVin(d),
      dRego(d),
      dMake(d),
      dModel(d),
      dColour(d),
      spec(d),
      d.seller_name || '',
      d.purchase_reference || '',
      String(dYear(d) || ''),
    ]
      .join(' ')
      .toLowerCase()
      .includes(t)
  }

  /*
   * One filter per column. The value compared is the same string the cell renders,
   * so what you type matches what you can see.
   */
  const columnText = (d: Deal, key: string): string => {
    const c = COLUMNS.find((x) => x.key === key)
    return c ? c.text(d) : ''
  }

  const matchesColumnFilters = (d: Deal) =>
    Object.entries(colFilters).every(([key, term]) => {
      const t = term.trim().toLowerCase()
      if (!t) return true
      return columnText(d, key).toLowerCase().includes(t)
    })

  const filtered = stock
    .filter((d) => (stage === 'all' ? true : d.deal_status === stage))
    .filter(matchesChip)
    .filter(matchesSearch)
    .filter(matchesColumnFilters)

  const sorted = [...filtered].sort((a, b) => {
    const dir = sortAsc ? 1 : -1
    const col = COLUMNS.find((c) => c.key === sortKey) || COLUMNS[0]
    const va = col.sort(a)
    const vb = col.sort(b)
    if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir
    return String(va).localeCompare(String(vb)) * dir
  })

  const total = sorted.length
  const pageCount = Math.max(1, Math.ceil(total / pageSize))
  const current = Math.min(page, pageCount)
  const start = (current - 1) * pageSize
  const rows = sorted.slice(start, start + pageSize)

  const sortBy = (key: SortKey) => {
    if (key === sortKey) setSortAsc(!sortAsc)
    else {
      setSortKey(key)
      setSortAsc(true)
    }
    setPage(1)
  }

  const arrow = (key: SortKey) => (sortKey === key ? (sortAsc ? ' ▲' : ' ▼') : '')

  /*
   * Winnie asked to be able to get this list out of the screen. It exports what
   * is on screen - after the chip, the search and the sort - and not the whole
   * table, because a file that quietly disagrees with the list you were looking
   * at is worse than no file at all.
   */
  const exportCsv = () => {
    const cell = (v: unknown) => {
      const s = v == null ? '' : String(v)
      return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s
    }
    const header = COLUMNS.map((c) => c.label)
    /*
     * Money and odometer cells are exported unformatted. A spreadsheet that reads
     * "$21,000" as text rather than a number is not much use for reconciling.
     */
    const lines = sorted.map((d) =>
      COLUMNS.map((c) => (c.num ? c.text(d).replace(/[$,]/g, '') : c.text(d)))
        .map(cell)
        .join(','),
    )
    const body = [header.map(cell).join(','), ...lines].join('\n')
    const url = URL.createObjectURL(new Blob([body], { type: 'text/csv;charset=utf-8' }))
    const a = document.createElement('a')
    a.href = url
    a.download = 'stock-' + new Date().toISOString().slice(0, 10) + '.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  const runSearch = () => {
    setApplied(typed.trim())
    setPage(1)
  }

  const setChipAndReset = (c: Chip) => {
    setChip(c)
    setPage(1)
  }

  return (
    <div className="stock-view">
      <div className="stock-title">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
          <path d="M5 17h14M4 17v-4l2-5h12l2 5v4M4 17v2M20 17v2" />
          <circle cx="7.5" cy="17" r="1.6" />
          <circle cx="16.5" cy="17" r="1.6" />
        </svg>
        <h2>Stock Management</h2>
      </div>

      {/* The two create-vehicle buttons were removed per Winnie's review: stock is
          never created by hand, it arrives from Autograb Sourcing and the Deal
          Pipeline. Both buttons only ever showed an explanatory banner. */}
      <div className="stock-tabstrip">
        <span className="stock-tab is-active">List</span>
        <span className="stock-tab-note">
          Vehicles enter stock automatically once a deal is paid.
        </span>
      </div>

      {banner ? (
        <div className="stock-banner" onClick={() => setBanner(null)}>
          {banner}
          <span className="stock-banner-x">close</span>
        </div>
      ) : null}

      <div className="stock-searchbar">
        <div className="stock-search-fields">
          <div className="stock-search-wrap">
            <input
              type="text"
              className="stock-search-input"
              placeholder="Search by Stock No, Make, Model, Colour, VIN, Year, Seller"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') runSearch()
              }}
            />
            {typed || applied ? (
              <button
                className="stock-search-clear"
                title="Clear search"
                onClick={() => {
                  setTyped('')
                  setApplied('')
                  setPage(1)
                }}
              >
                &#9587;
              </button>
            ) : null}
          </div>
          <select
            className="stock-select"
            value={stage}
            onChange={(e) => {
              setStage(e.target.value)
              setPage(1)
            }}
          >
            <option value="all">All Stages</option>
            {STAGES.filter((s) => STOCK_REGISTER_STAGES.includes(s.id)).map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
        <div className="stock-search-buttons">
          <button className="sm-btn sm-btn-primary" onClick={runSearch}>
            Search
          </button>
          <button
            className="sm-btn sm-btn-amber"
            onClick={() => setBanner('Advanced Search is not built yet. The quick search covers stock no, VIN, make, model, colour, year and seller.')}
          >
            Advanced Search
          </button>
        </div>
      </div>

      <div className="stock-quickrow">
        <span className="stock-quicklabel">Quick Search</span>
        <div className="stock-chips">
          {CHIPS.map((c) => (
            <button
              key={c.id}
              className={'stock-chip' + (chip === c.id ? ' is-active' : '')}
              onClick={() => setChipAndReset(c.id)}
            >
              {c.label}
            </button>
          ))}
        </div>
        <div className="stock-quickright">
          <span className="stock-key">
            Key: No PPSR <em className="stock-flag stock-flag-p">!</em>
          </span>
          <button
            className={'stock-density' + (compact ? '' : ' is-active')}
            title="Comfortable rows"
            onClick={() => setCompact(false)}
          >
            &#9776;
          </button>
          <button
            className={'stock-density' + (compact ? ' is-active' : '')}
            title="Compact rows"
            onClick={() => setCompact(true)}
          >
            &#8801;
          </button>
          <button className="sm-view-btn" title="Download this list as a CSV" onClick={exportCsv}>
            Export
          </button>
        </div>
      </div>

      {loading ? (
        <p className="stock-empty">Loading stock...</p>
      ) : (
        <>
          <div className="stock-tablewrap">
            <table className={'stock-table' + (compact ? ' is-compact' : '')}>
              <thead>
                <tr>
                  {COLUMNS.map((c) => (
                    <th
                      key={c.key}
                      className={c.num ? 'stock-num' : undefined}
                      onClick={() => sortBy(c.key)}
                    >
                      {c.label}
                      {arrow(c.key)}
                    </th>
                  ))}
                </tr>
                <tr className="stock-filterrow">
                  {COLUMNS.map((c, i) => (
                    <th key={c.key}>
                      <input
                        className="stock-colfilter"
                        value={colFilters[c.key] || ''}
                        placeholder={c.label}
                        aria-label={'Filter by ' + c.label}
                        onChange={(e) => setColFilters({ ...colFilters, [c.key]: e.target.value })}
                      />
                      {i === COLUMNS.length - 1 && Object.values(colFilters).some((v) => v.trim()) ? (
                        <button
                          type="button"
                          className="stock-colfilter-clear"
                          onClick={() => setColFilters({})}
                        >
                          Clear
                        </button>
                      ) : null}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={COLUMNS.length} className="stock-empty">
                      No vehicles match these filters.
                    </td>
                  </tr>
                ) : (
                  rows.map((d) => (
                    <tr key={d.id} onClick={() => setSelectedDeal(d)}>
                      {COLUMNS.map((c) => (
                        <td key={c.key} className={c.num ? 'stock-num' : undefined}>
                          {c.render ? c.render(d) : c.text(d) || '\u2014'}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="stock-footer">
            <div className="stock-pager">
              <button disabled={current === 1} onClick={() => setPage(1)} title="First page">
                &#8676;
              </button>
              <button disabled={current === 1} onClick={() => setPage(current - 1)} title="Previous page">
                &#8592;
              </button>
              <span className="stock-pagenum">{current}</span>
              <button
                disabled={current === pageCount}
                onClick={() => setPage(current + 1)}
                title="Next page"
              >
                &#8594;
              </button>
              <button
                disabled={current === pageCount}
                onClick={() => setPage(pageCount)}
                title="Last page"
              >
                &#8677;
              </button>
              <select
                className="stock-pagesize"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value))
                  setPage(1)
                }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span className="stock-pagesize-label">items per page</span>
            </div>
            <div className="stock-showing">
              {total === 0
                ? 'No items'
                : 'Showing ' + (start + 1) + ' - ' + Math.min(start + pageSize, total) + ' of ' + total + ' items'}
            </div>
          </div>
        </>
      )}

      {selectedDeal ? (
        <DealWorkspace
          user={user}
          deal={selectedDeal}
          onClose={() => setSelectedDeal(null)}
          onChanged={async () => {
            const id = selectedDeal.id
            const { data } = await supabase.from('deals').select('*').eq('id', id).maybeSingle()
            if (data) setSelectedDeal(data as Deal)
            fetchStock()
          }}
        />
      ) : null}
    </div>
  )
}
